// Pure: decoded features (+ optional color-by column) → the `SpatialMapLayerInput[]` the shared map
// renders, plus the legend + a feature→layer index. Color-by is realized by SPLITTING features into
// one layer per category (each layer painted with its category colour from `categoryColorScale`) —
// this reuses the map's per-layer colour with ZERO new map plumbing. No color-by → one layer in the
// palette's first colour and NO legend. Eager-safe: only mapThemes (no maplibre) is imported.

import type { SpatialMapFeature, SpatialMapLayerInput } from '../map/spatialMapTypes';
import type { SpatialLayerDefinition } from '../types/spatialTypes';
import {
  SPATIAL_LAYER_PALETTE,
  categoryColorScale,
  type CategoryLegendEntry,
} from '../map/mapThemes';

export type ResultLayers = {
  layers: SpatialMapLayerInput[];
  /** Non-null only while color-by is active — the panel renders it as the legend. */
  legend: CategoryLegendEntry[] | null;
  /** feature id → the layer id it lives in (for the selection halo). */
  layerIdForFeature: Map<string | number, string>;
};

const BASE_LAYER_ID = 'spatial-results';

function makeLayerDef(id: string, color: string, cap: number): SpatialLayerDefinition {
  return {
    id,
    label: 'Results',
    visible: true,
    source: { kind: 'query', sql: '', geometryRef: { kind: 'column', column: 'geom' } },
    style: { color },
    featureCap: cap,
  };
}

/** Sanitize a category label into a stable, id-safe layer suffix. */
function layerSuffix(category: string, index: number): string {
  const safe = category.replace(/[^a-zA-Z0-9_-]/gu, '_').slice(0, 40);
  return `${index}-${safe}`;
}

export function buildResultLayers(params: {
  features: SpatialMapFeature[];
  cap: number;
  colorByColumnId: string | null;
}): ResultLayers {
  const { features, cap, colorByColumnId } = params;

  if (!colorByColumnId) {
    const layerIdForFeature = new Map<string | number, string>();
    for (const f of features) layerIdForFeature.set(f.id, BASE_LAYER_ID);
    return {
      layers: [{ def: makeLayerDef(BASE_LAYER_ID, SPATIAL_LAYER_PALETTE[0], cap), features }],
      legend: null,
      layerIdForFeature,
    };
  }

  const scale = categoryColorScale(features.map((f) => f.properties?.[colorByColumnId]));
  const grouped = new Map<string, SpatialMapFeature[]>();
  for (const f of features) {
    const category = scale.categoryOf(f.properties?.[colorByColumnId]);
    const bucket = grouped.get(category);
    if (bucket) bucket.push(f);
    else grouped.set(category, [f]);
  }

  const layers: SpatialMapLayerInput[] = [];
  const layerIdForFeature = new Map<string | number, string>();
  scale.legend.forEach((entry, index) => {
    const bucket = grouped.get(entry.category);
    if (!bucket || bucket.length === 0) return;
    const layerId = `${BASE_LAYER_ID}-${layerSuffix(entry.category, index)}`;
    layers.push({ def: makeLayerDef(layerId, entry.color, cap), features: bucket });
    for (const f of bucket) layerIdForFeature.set(f.id, layerId);
  });

  return { layers, legend: scale.legend, layerIdForFeature };
}
